// Пункт протокола в режиме ведения (011: data-model.md, «Пункт — minutes.items»; contracts/api.md,
// ItemRow). Постоянный номер — uuid; родитель и позиция задают дерево; источник из файла
// не меняется (принцип I, ADR-0031).
import { z } from '../kernel/zod.ts';
import { ItemAnswerMarkSchema } from './answers.ts';
import { DateSchema, IdSchema, TimestampSchema } from '../kernel/common.ts';

/** Ключ дополнительного поля протокола: неизменяем, подпись меняется отдельно. */
export const EXTRA_FIELD_KEY_PATTERN = /^x[0-9]{1,3}$/;
export const ExtraFieldKeySchema = z.string().regex(EXTRA_FIELD_KEY_PATTERN);

/**
 * Управляющие знаки C0 и DEL, кроме табуляции и переводов строки: в тексте пункта их нет —
 * команда с ними отклоняется (REC-007), перевод убирает их из полей (сходимость 011, T046).
 */
// Без флага g: у такого выражения нет состояния lastIndex, и `.test()` в чужом коде не
// пропускает каждое второе совпадение (ревью 011).
// eslint-disable-next-line no-control-regex -- управляющие знаки и есть предмет проверки
export const CONTROL_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/;

const CONTROL_CHARS_ALL = new RegExp(CONTROL_CHARS.source, 'g');

/** Текст без управляющих знаков (CONTROL_CHARS). */
export function withoutControlChars(text: string): string {
  return text.replace(CONTROL_CHARS_ALL, '');
}

/**
 * Пределы правки полей пункта в команде (contracts/api.md, «Схемы»). Значения из файла при
 * переводе не обрезаются: в ответах пределов нет.
 */
export const ITEM_TITLE_MAX = 2000;
export const ITEM_NOTE_MAX = 20000;
/** Ответственных у пункта (014: FR-007). */
export const ITEM_ASSIGNEES_MAX = 50;
/** Контрагентов у пункта (027: FR-003) — как ответственных. */
export const ITEM_CONTRACTORS_MAX = 50;

/**
 * Состояние иерархии: resolved — родитель доказан или выбран человеком; uncertain — родитель
 * не определён, есть кандидаты; partial — сам пункт определён, но неопределён один из предков
 * (DATA-09, IMPORT-25).
 */
export const itemHierarchyStates = ['resolved', 'uncertain', 'partial'] as const;
export type ItemHierarchyState = (typeof itemHierarchyStates)[number];
export const ItemHierarchyStateSchema = z.enum(itemHierarchyStates);

/** Кандидат в родители: пункт того же протокола или корень (itemId = null). */
export const ParentCandidateSchema = z.object({
  itemId: IdSchema.nullable(),
  title: z.string(),
});
export type ParentCandidate = z.infer<typeof ParentCandidateSchema>;

export const ItemHierarchySchema = z.object({
  state: ItemHierarchyStateSchema,
  candidates: z.array(ParentCandidateSchema),
  reason: z.string().nullable(),
});
export type ItemHierarchy = z.infer<typeof ItemHierarchySchema>;

/**
 * Поля пункта, которые правятся командами ячеек (011: FR-030; 014: FR-007, FR-014, FR-020,
 * FR-026). Дополнительное поле — `extra.<ключ>`. `responsibleRaw` — текст ответственного
 * протоколов 011–013; с 014 колонка «Ответственный» — `assignees` (research, Р1), а
 * `responsibleRaw` остаётся для совместимости до сходимости среза. 021: «Подрядчик / факт»
 * (`contractorActual`) разделён на «Подрядчик» и «Фактическое окончание» (`actualEndRaw`).
 */
export const editableItemFields = [
  'title',
  'numberRaw',
  'noteHtml',
  'dueRaw',
  'responsibleRaw',
  'assignees',
  'startRaw',
  'contractor',
  'actualEndRaw',
  'workState',
  'workNote',
] as const;
export type EditableItemField = (typeof editableItemFields)[number];

/** Встроенные поля одним выражением — источник для ключей ячеек, оформления и колонок вида. */
const BUILTIN_FIELD_SOURCE = editableItemFields.join('|');

export const ItemFieldSchema = z.union([
  z.enum(editableItemFields),
  z.string().regex(/^extra\.x[0-9]{1,3}$/),
]);
export type ItemField = z.infer<typeof ItemFieldSchema>;

/** Ключ поля пункта как строка: встроенное поле или `extra.<ключ>` (ключи оформления, 013). */
export const ITEM_FIELD_KEY_PATTERN = new RegExp(`^(${BUILTIN_FIELD_SOURCE}|extra\\.x[0-9]{1,3})$`);

/** Ключ колонки в виде: встроенное поле или ключ дополнительного поля (013; 014 — один источник). */
export const COLUMN_KEY_PATTERN = new RegExp(`^(${BUILTIN_FIELD_SOURCE}|x[0-9]{1,3})$`);

/**
 * Предел значения поля в команде ячеек (contracts/api.md, «Поля пункта»). «Контрагент» с 027 —
 * список, как «Ответственный»: текст ячейки — до ITEM_TITLE_MAX, число частей — пределом списка.
 */
export function fieldValueMax(field: string): number {
  if (field === 'noteHtml') return ITEM_NOTE_MAX;
  return ITEM_TITLE_MAX;
}

/** Поля пункта со списком записей справочника или текстов (027: FR-003). */
export const partyFields = ['assignees', 'contractor'] as const;
export type PartyField = (typeof partyFields)[number];

/** Вид записи справочника, которую принимает поле (027: FR-001, FR-003). */
export type PartyKind = 'person' | 'organization';

/** «Ответственный» — люди, «Контрагент» — организации (027: решение владельца 28.10.2026). */
export function partyKindOf(field: PartyField): PartyKind {
  return field === 'assignees' ? 'person' : 'organization';
}

export function isPartyField(field: string): field is PartyField {
  return field === 'assignees' || field === 'contractor';
}

/**
 * Состояние работы (014: FR-020; конституция 2.2.0, «Статусы на экране»): выбор человека,
 * отдельно от признака контроля. NULL — «Не задан».
 */
export const workStates = [
  'not_started',
  'in_progress',
  'partial',
  'paused',
  'done',
  'cancelled',
] as const;
export type WorkState = (typeof workStates)[number];
export const WorkStateSchema = z.enum(workStates);

export const workStateLabels = {
  not_started: 'Не начато',
  in_progress: 'В работе',
  partial: 'Частично выполнено',
  paused: 'Приостановлено',
  done: 'Выполнено',
  cancelled: 'Отменено',
} as const satisfies Record<WorkState, string>;

/** Подпись состояния «не выбрано» (приглушённым текстом, не бейджем). */
export const WORK_STATE_UNSET_LABEL = 'Не задан';

/**
 * Пометка элемента списка (014: research, Р1; 027: FR-005, Р-14): текст совпал с несколькими
 * записями (`ambiguous`) или только с архивной (`archived`); `organization` / `person` — текст или
 * запись другого вида, чем у поля: организация в «Ответственном», человек в «Контрагенте».
 */
export const partyMarks = ['ambiguous', 'archived', 'organization', 'person'] as const;
export const PartyMarkSchema = z.enum(partyMarks);
export type PartyMark = z.infer<typeof PartyMarkSchema>;

/**
 * Элемент «Ответственного» или «Контрагента» в ответе (014: research, Р1; 027: research, Р1):
 * запись справочника или текст — запись из Excel, часть, совпавшая с несколькими записями,
 * текст другого вида. У записи пометка — только вид (запись сменила вид после выбора).
 */
export const PartySchema = z.object({
  recordId: IdSchema.nullable(),
  kind: z.enum(['person', 'organization']).nullable(),
  name: z.string(),
  raw: z.string().nullable(),
  /** Запись в архиве справочника — «Отключён». */
  archived: z.boolean(),
  /** Пришёл из файла при переводе (ключ упоминания). */
  fromFile: z.boolean(),
  mark: PartyMarkSchema.nullable(),
});
export type Party = z.infer<typeof PartySchema>;
/** Ответственный пункта — элемент списка «Ответственный» (014). */
export const AssigneeSchema = PartySchema;
export type Assignee = Party;

/**
 * Контроль из файла — разбор исходной строки версии (014: research, Р2): срок и переносы
 * толкуются так, пока срок не правили; пауза и закрытие — пока состояние «Не задан».
 */
export const FileControlSchema = z.object({
  /**
   * Текст срока при переводе (сходимость 014, T040): срок толкуется по разбору файла, пока
   * `ItemRow.dueRaw` равен этому тексту, — и после отмены правки.
   */
  dueText: z.string().nullable().optional(),
  due: DateSchema.nullable(),
  transfers: z.array(DateSchema),
  paused: z.boolean(),
  closed: z.boolean(),
});
export type FileControl = z.infer<typeof FileControlSchema>;

/** Цвет оформления ячейки — `#RRGGBB` верхним регистром (013: research, Р5). */
export const CellColorSchema = z.string().regex(/^#[0-9A-F]{6}$/);

/**
 * Оформление ячейки (поля пункта), отдельно от оформления фраз примечания (013: FR-015,
 * data-model.md): жирный, курсив, зачёркнутый, цвет текста, заливка. Явное `b: false` хранится —
 * обычный вес у строки-ветви; курсив и зачёркнутый — только `true`.
 */
/** Стили линии границы (017: research, Р1): прочие стили Excel сводятся к ближайшему. */
export const borderLineStyles = ['thin', 'medium', 'thick', 'double', 'dashed', 'dotted'] as const;
export const BorderLineStyleSchema = z.enum(borderLineStyles);
export type BorderLineStyle = z.infer<typeof BorderLineStyleSchema>;

/** Линия стороны ячейки: стиль и цвет (017: FR-001, FR-004). */
export const BorderLineSchema = z.strictObject({ s: BorderLineStyleSchema, c: CellColorSchema });
export type BorderLine = z.infer<typeof BorderLineSchema>;

/**
 * Границы ячейки по сторонам — верх, право, низ, лево (017: research, Р1, Р3). Сторона без
 * линии — без ключа; пустой объект не хранится.
 */
export const CellBorderSchema = z.strictObject({
  t: BorderLineSchema.optional(),
  r: BorderLineSchema.optional(),
  b: BorderLineSchema.optional(),
  l: BorderLineSchema.optional(),
});
export type CellBorder = z.infer<typeof CellBorderSchema>;

export const CellStyleSchema = z.strictObject({
  b: z.boolean().optional(),
  i: z.literal(true).optional(),
  s: z.literal(true).optional(),
  color: CellColorSchema.optional(),
  fill: CellColorSchema.optional(),
  /** Границы ячейки (017): часть оформления — буфер переносит, «Снять оформление» снимает. */
  border: CellBorderSchema.optional(),
});
export type CellStyle = z.infer<typeof CellStyleSchema>;

/** Роль ячейки в объединении по направлению (017: research, Р2). */
export const MergePartSchema = z.enum(['start', 'cont']);
export type MergePart = z.infer<typeof MergePartSchema>;

/**
 * Роль ячейки в объединении: `v` — по вертикали (цепочка пунктов в порядке протокола), `h` — по
 * горизонтали (цепочка полей в порядке колонок схемы); у прямоугольника — обе (017: data-model.md).
 */
export const CellMergeRoleSchema = z
  .strictObject({ v: MergePartSchema.optional(), h: MergePartSchema.optional() })
  .refine((role) => role.v !== undefined || role.h !== undefined, 'Пустая роль не хранится');
export type CellMergeRole = z.infer<typeof CellMergeRoleSchema>;

/** Роли ячеек пункта по ключам полей; ячейка вне объединения — без ключа. */
export const CellMergesSchema = z.record(
  z.string().regex(ITEM_FIELD_KEY_PATTERN),
  CellMergeRoleSchema,
);
export type CellMerges = z.infer<typeof CellMergesSchema>;

/** Оформление пункта по ключам полей; поля без оформления в объекте нет. */
export const CellStylesSchema = z.record(z.string().regex(ITEM_FIELD_KEY_PATTERN), CellStyleSchema);
export type CellStyles = z.infer<typeof CellStylesSchema>;

/** Строка пункта в ответах (ItemRow). */
export const ItemRowSchema = z.object({
  id: IdSchema,
  parentId: IdSchema.nullable(),
  /**
   * Дробный ключ среди соседей (research, Р2). Длина не ограничена: ключ растёт при вставках
   * в один промежуток, и ответ с длинным ключом не должен ломать чтение протокола.
   */
  position: z.string().min(1),
  title: z.string(),
  numberRaw: z.string().nullable(),
  /** Канонический rich text: подмножество HTML (research, Р6). */
  noteHtml: z.string(),
  dueRaw: z.string().nullable(),
  responsibleRaw: z.string().nullable(),
  extra: z.record(ExtraFieldKeySchema, z.string()),
  /** Скрыта в Excel — исходный признак, не меняется. */
  sourceHidden: z.boolean(),
  /** Вид строки 1.7 при переводе; null — пункт создан в таблице. */
  sourceKind: z.string().nullable(),
  hierarchy: ItemHierarchySchema,
  /**
   * Наследование объединения по вертикали: поле → адрес первой ячейки объединения («C326»).
   * Значение поля — из неё; правка поля снимает наследование (граничный случай C326:C328).
   */
  inherited: z.record(z.string(), z.string()),
  /**
   * Ревизия последнего изменения каждого поля (research, Р3); служебные ключи `place` (родитель
   * и место) и `archived` (архив и возврат) — для конфликта отмены (012: research, Р6).
   */
  fieldRevs: z.record(z.string(), z.number().int().min(1)),
  updatedRevision: z.number().int().min(1),
  /** Исходный пункт, если пункт создан разделением (012: TREE-13, DATA-10). */
  splitFromId: IdSchema.nullable(),
  /** Время архива; null — пункт действующий (012: TREE-12). */
  archivedAt: TimestampSchema.nullable(),
  /** Оформление ячеек по полям (013: FR-015, FR-040). */
  styles: CellStylesSchema,
  /** Объединённые ячейки — роли по полям (017); в снимках до 017 нет — пусто. */
  merges: CellMergesSchema.default({}),
  /** «Скрыто в протоколе» — у всех, отдельно от «скрыта в Excel» и «скрыто мной» (013: FR-031). */
  hiddenInProtocol: z.boolean(),
  /** Ответственные по порядку (014: FR-007). */
  assignees: z.array(AssigneeSchema),
  /** «Начало» — гибкий срок текстом (014: FR-014). */
  startRaw: z.string().nullable(),
  /** Контрагенты по порядку (027: FR-003): организации справочника или тексты. */
  contractor: z.array(PartySchema),
  /**
   * «Фактическое окончание» — гибкий срок текстом (021: FR-001). Из файла — дата колонки
   * подрядчика (двойной) или колонки с ролью «Фактическое окончание».
   */
  actualEndRaw: z.string().nullable(),
  /** Состояние работы; null — «Не задан» (014: FR-020). */
  workState: WorkStateSchema.nullable(),
  workNote: z.string().nullable(),
  /** Прежние тексты срока, добавленные правками (переносы в режиме ведения, research Р3). */
  dueTransfers: z.array(z.string()),
  /** Контроль из файла; null — пункт создан в таблице или протокол без файла. */
  fileControl: FileControlSchema.nullable(),
  /**
   * 030 (FR-040): ответ ответственного после последнего снимка недели — пометка «ответил: …»;
   * нет ответа — null. Только в состоянии таблицы (minutes.state); в снимках и ответах команд —
   * нет поля.
   */
  answer: ItemAnswerMarkSchema.nullable().optional(),
  /** 030 (FR-036): открыт запрос «Проверить связь упоминания» — «Назначение оспорено». */
  disputed: z.boolean().optional(),
});
export type ItemRow = z.infer<typeof ItemRowSchema>;

/**
 * Поля пункта 014 нового пункта: без ответственных, «Не задан», без переносов и контроля из
 * файла (data-model.md). Общие значения для сервера, веба и подменных серверов.
 */
export const itemFieldDefaults = {
  assignees: [],
  startRaw: null,
  contractor: [],
  actualEndRaw: null,
  workState: null,
  workNote: null,
  dueTransfers: [],
  fileControl: null,
} satisfies Pick<
  ItemRow,
  | 'assignees'
  | 'startRaw'
  | 'contractor'
  | 'actualEndRaw'
  | 'workState'
  | 'workNote'
  | 'dueTransfers'
  | 'fileControl'
>;
